import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card, { CardHeader, CardBody } from '../ui/Card';

interface Report {
  period: string;
  type?: string;
  filingDate: string;
  balanceSheet?: {
    totalAssets?: string | number | null;
    totalLiabilities?: string | number | null;
    shareholdersEquity?: string | number | null;
  };
  metrics?: {
    totalAssets?: string | number | null;
    totalLiabilities?: string | number | null;
    shareholdersEquity?: string | number | null;
  };
}

interface Props {
  reports: Report[];
  compact?: boolean;
}

// Parse currency value to number (in billions)
const parseCurrency = (value?: string | number | null): number | null => {
  if (!value) return null;

  // If already a number, convert to billions
  if (typeof value === 'number') {
    return value / 1_000_000_000;
  }

  // If not a string, return null
  if (typeof value !== 'string') return null;

  // Remove $ and commas
  const clean = value.replace(/[$,]/g, '').trim();

  // Handle B for billions, M for millions
  if (clean.includes('B') || clean.includes('b')) {
    return parseFloat(clean.replace(/[Bb]/g, ''));
  } else if (clean.includes('M') || clean.includes('m')) {
    return parseFloat(clean.replace(/[Mm]/g, '')) / 1000;
  } else {
    const num = parseFloat(clean);
    return isNaN(num) ? null : num / 1_000_000_000;
  }
};

// Format number for display (in billions)
const formatBillions = (value: number | null): string => {
  if (value === null) return 'N/A';
  return `$${value.toFixed(2)}B`;
};

export default function BalanceSheetTrends({ reports, compact = false }: Props) {
  const [viewMode, setViewMode] = useState<'annual' | 'quarterly'>('annual');

  // Filter by report type
  const filteredReports = reports.filter(r => {
    if (viewMode === 'annual') {
      return r.type === 'Annual' || r.type === '10-K' || (r.period && (r.period.includes('FY') || r.period.toLowerCase().includes('annual')));
    }
    return r.type === 'Quarterly' || r.type === '10-Q' || (r.period && /Q[1-4]/.test(r.period));
  });

  // Transform reports data for Recharts — skip reports with no balance sheet data
  // Try balanceSheet first (new structure), fall back to metrics (old structure) for backward compatibility
  const chartData = filteredReports
    .map((report) => {
      const source = report.balanceSheet || report.metrics || {};
      return {
        period: report.period,
        totalAssets: parseCurrency(source.totalAssets),
        totalLiabilities: parseCurrency(source.totalLiabilities),
        shareholdersEquity: parseCurrency(source.shareholdersEquity),
      };
    })
    .filter((d) => d.totalAssets !== null || d.totalLiabilities !== null || d.shareholdersEquity !== null)
    .reverse(); // Reverse to show oldest to newest

  // Check if we have any balance sheet data
  const hasData = chartData.some(
    (item) => item.totalAssets !== null || item.totalLiabilities !== null || item.shareholdersEquity !== null
  );

  if (chartData.length === 0 || !hasData) {
    return (
      <Card>
        <CardHeader>
          <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-neutral-900`}>
            Balance Sheet Trends
          </h3>
          <p className="text-sm text-neutral-600 mt-1">
            Historical balance sheet metrics across reporting periods
          </p>
        </CardHeader>
        <CardBody>
          <div className="text-center py-12 text-neutral-500">
            <p>No balance sheet trend data available</p>
            <p className="text-sm mt-2">Balance sheet metrics will appear here once available</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-neutral-900`}>
              Balance Sheet Trends
            </h3>
            {!compact && (
              <p className="text-sm text-neutral-600 mt-1">
                Historical balance sheet metrics across reporting periods (in billions)
              </p>
            )}
          </div>
          <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
            <button
              onClick={() => setViewMode('annual')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'annual'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Annual
            </button>
            <button
              onClick={() => setViewMode('quarterly')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'quarterly'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Quarterly
            </button>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={compact ? 280 : 400}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis
              dataKey="period"
              stroke="#737373"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#737373"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `$${value.toFixed(1)}B`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e5e5',
                borderRadius: '6px',
                fontSize: '12px',
              }}
              formatter={(value: any) => formatBillions(value as number | null)}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="totalAssets"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Total Assets"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="totalLiabilities"
              stroke="#f59e0b"
              strokeWidth={2}
              name="Total Liabilities"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="shareholdersEquity"
              stroke="#10b981"
              strokeWidth={2}
              name="Shareholders Equity"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Summary Statistics */}
        <div className="mt-4 pt-3 border-t border-neutral-200">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Total Assets', key: 'totalAssets' as const, color: 'text-primary-600' },
              { label: 'Liabilities', key: 'totalLiabilities' as const, color: 'text-orange-600' },
              { label: 'Equity', key: 'shareholdersEquity' as const, color: 'text-green-600' },
            ].map(({ label, key, color }) => {
              const latest = chartData[chartData.length - 1]?.[key] as number | null;
              const previous = chartData.length >= 2 ? chartData[chartData.length - 2]?.[key] as number | null : null;
              const change = (latest != null && previous != null && previous !== 0)
                ? ((latest - previous) / Math.abs(previous)) * 100 : null;
              return (
                <div key={key}>
                  <div className={`${compact ? 'text-[10px]' : 'text-xs'} font-medium text-neutral-500`}>{label}</div>
                  <div className={`${compact ? 'text-sm' : 'text-lg'} font-bold ${color} tabular-nums`}>
                    {formatBillions(latest)}
                  </div>
                  {change != null && (
                    <div className={`text-[10px] font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% vs prior
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Accounting Equation Check */}
          {chartData.length > 0 && (() => {
            const latest = chartData[chartData.length - 1];
            const assets = latest?.totalAssets;
            const liabilities = latest?.totalLiabilities;
            const equity = latest?.shareholdersEquity;
            if (assets == null || liabilities == null || equity == null) return null;
            const diff = Math.abs(assets - (liabilities + equity));
            const isBalanced = diff < 0.1;
            return (
              <div className={`mt-3 pt-3 border-t border-neutral-100 flex items-center gap-2 ${compact ? 'text-[10px]' : 'text-xs'} text-neutral-500`}>
                <span>A = L + E</span>
                <span className={`px-1.5 py-0.5 rounded font-semibold ${
                  isBalanced ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {isBalanced ? 'Balanced' : `Δ ${formatBillions(diff)}`}
                </span>
              </div>
            );
          })()}
        </div>
      </CardBody>
    </Card>
  );
}
